/**
 * Composite-score ranking for the AI Scanner (#736).
 *
 * Turns the hashtag-filtered pool plus the member's Q1/Q2 answers into a
 * ranked ordering. Deterministic and rule-based — no LLM (NFR-6), no network,
 * no store: every input is passed in, so the whole engine is unit-testable as
 * a pure function.
 *
 * The method is Simple Additive Weighting (SAW):
 *
 *   1. Map each answer to its scan (`getAnswerScans`, one scan per answer).
 *   2. NORMALIZE each scan across the pool to a percentile rank in [0, 1].
 *   3. COMBINE: `composite = w_q1 · norm_q1 + w_q2 · norm_q2`, weights summing
 *      to 1 (default 0.5 / 0.5).
 *   4. RANK by composite descending, ties keeping the pool's existing order.
 *
 * Every scan in the mapping is higher-is-better, so no direction inversion is
 * applied anywhere — see `scoringScans.js` for why that is a property of the
 * mapping rather than a per-scan field.
 *
 * WHY PERCENTILE RANK rather than min-max: the scoring scans are a mix of
 * bounded 0-100 scores (`SizeScore`, `TotalScore`) and unbounded, heavily
 * skewed ratios (`DividendYield`, `NetSalesChange`). Min-max on the latter
 * lets a single outlier — one symbol at 400% net-sales growth — compress every
 * other symbol into the bottom few percent of the axis, so that axis stops
 * discriminating and the composite collapses onto the other one. Percentile
 * rank depends only on ORDER, so it is unaffected by the magnitude of an
 * outlier, and it puts the two axes on genuinely comparable footing before
 * they are summed — which is what makes a 0.5/0.5 weighting mean what it says.
 *
 * @see src/constants/scoringScans.js for the Q1/Q2 → scan mapping
 * @see src/utils/scanCandidates.js for the selection seam that consumes this
 */

import { getAnswerScans } from '../constants/scoringScans';

/**
 * Axis weights, summing to 1. Tunable here rather than at a call site so the
 * "what does the ranking weigh?" question has exactly one answer.
 *
 * 0.5 / 0.5 is the deliberate default: nothing in the member's answers says
 * one axis matters more than the other — Q1 picks a goal and Q2 an appetite,
 * and both are stated with the same confidence — so weighting either above the
 * other would be inventing a preference they did not express.
 *
 * @type {Readonly<{q1: number, q2: number}>}
 */
export const DEFAULT_AXIS_WEIGHTS = Object.freeze({ q1: 0.5, q2: 0.5 });

/**
 * Reads a symbol's raw value for one scan out of cached scan data.
 *
 * Mirrors `hashtagFilterEngine`'s read: scan data is keyed by LOWERCASED scan
 * name, and the per-symbol entry carries `filterValue`.
 *
 * @param {object} scanData - `state.scans.scanData`.
 * @param {?string} scanName - Scan name, or null for an unresolved axis.
 * @param {string} symbolId
 * @returns {?number} the finite value, or null when the scan is not cached,
 *   the symbol is absent from it, or the value is not a finite number.
 */
export function readScanValue(scanData, scanName, symbolId) {
  if (!scanData || !scanName) return null;
  const entry = scanData[String(scanName).toLowerCase()];
  const symbols = entry && entry.symbols;
  const hit = symbols && symbols[symbolId];
  if (!hit || typeof hit !== 'object') return null;
  const raw = hit.filterValue;
  if (raw === null || raw === undefined || raw === '') return null;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * Percentile rank of every value in a pool, as `(below + 0.5·equal) / n`.
 *
 * The mid-rank ("Hazen") form, so a run of tied values all share one score
 * centred on the span they occupy, rather than the whole run inheriting the
 * best or worst position in it. Tied inputs therefore produce tied outputs,
 * which is what keeps the pool's original order intact through the sort.
 *
 * A MISSING value (`null`) ranks below every present one and ties with the
 * other missing ones. That is the whole of the "no value scores worst on that
 * axis" rule — it needs no special case downstream, and it stays deterministic
 * when an entire axis is missing (every symbol ties, so the axis contributes a
 * constant and the other axis decides the order).
 *
 * @param {Array<?number>} values - One entry per pool member, in pool order.
 * @returns {number[]} percentile ranks in [0, 1], in the same order. Empty in,
 *   empty out.
 */
export function percentileRanks(values) {
  const list = Array.isArray(values) ? values : [];
  const n = list.length;
  if (n === 0) return [];

  // Sorted once, then each distinct value is located by binary search, so the
  // whole pass is O(n log n) rather than the O(n²) a per-value scan would be.
  // A pool can be several thousand symbols wide before any hashtag narrows it.
  const present = list.filter((v) => v !== null).sort((a, b) => a - b);
  const missingCount = n - present.length;

  // Count of present values strictly below `value` / equal to it, via the
  // first and last insertion points for `value` in the sorted array.
  const lowerBound = (value) => {
    let lo = 0;
    let hi = present.length;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if (present[mid] < value) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  };
  const upperBound = (value) => {
    let lo = 0;
    let hi = present.length;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if (present[mid] <= value) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  };

  return list.map((value) => {
    if (value === null) {
      // Missing: nothing below it, tied with every other missing value.
      return (0.5 * missingCount) / n;
    }
    const below = lowerBound(value) + missingCount;
    const equal = upperBound(value) - lowerBound(value);
    return (below + 0.5 * equal) / n;
  });
}

/**
 * Normalizes the weights to sum to 1, so a caller cannot accidentally inflate
 * or deflate every composite by passing `{ q1: 1, q2: 1 }`. Falls back to the
 * default pair for anything unusable (non-finite, negative, or summing to 0),
 * because a ranking that silently produced `NaN` composites would order the
 * pool arbitrarily rather than fail.
 */
function normalizeWeights(weights) {
  const w = weights || DEFAULT_AXIS_WEIGHTS;
  const q1 = Number(w.q1);
  const q2 = Number(w.q2);
  if (!Number.isFinite(q1) || !Number.isFinite(q2) || q1 < 0 || q2 < 0) {
    return DEFAULT_AXIS_WEIGHTS;
  }
  const total = q1 + q2;
  if (total === 0) return DEFAULT_AXIS_WEIGHTS;
  return { q1: q1 / total, q2: q2 / total };
}

/**
 * Ranks a pool of symbols by composite score, best first.
 *
 * Pure and deterministic: the same pool, answers, scan data and weights always
 * produce the same ordering, right down to how ties fall. Ties keep the pool's
 * incoming order (which `filterSelectedHashtags` has already made MarketCap
 * descending) — there is no `MarketCap` tie-break of its own.
 *
 * An axis whose answer is unanswered or unmapped contributes nothing: its
 * weight is dropped and the remaining axis is re-normalized to 1, so a
 * mid-flow render (Q1 answered, Q2 not) ranks on Q1 alone rather than on half
 * a score. With neither axis resolved every composite is 0 and the pool comes
 * back in its incoming order.
 *
 * @param {string[]} pool - Symbol IDs, in the order the hashtag filter
 *   produced them.
 * @param {{q1: ?string, q2: ?string}} answers - Q1/Q2 option codes.
 * @param {object} scanData - `state.scans.scanData`.
 * @param {{q1: number, q2: number}} [weights] - Axis weights; defaults to
 *   {@link DEFAULT_AXIS_WEIGHTS}.
 * @returns {Array<{symbol: string, composite: number, axes: object}>} ranked
 *   best-first. `axes` carries, per question id, the `scan` that ranked it,
 *   the symbol's raw `value` (null when it had none) and its `normalized`
 *   percentile — everything needed to explain a placement.
 */
export function rankPool(pool, answers, scanData, weights) {
  const symbols = Array.isArray(pool) ? pool : [];
  if (symbols.length === 0) return [];

  const scans = getAnswerScans(answers);
  const weighting = normalizeWeights(weights);

  // Only axes with a resolved scan take part; their weights are rescaled to
  // sum to 1 so a single-axis ranking spans the same [0, 1] range as a
  // two-axis one and stays comparable against the same thresholds.
  const activeAxes = ['q1', 'q2'].filter((axis) => scans[axis] !== null);
  const activeWeight = activeAxes.reduce(
    (sum, axis) => sum + weighting[axis],
    0
  );

  const normalizedByAxis = {};
  activeAxes.forEach((axis) => {
    normalizedByAxis[axis] = percentileRanks(
      symbols.map((symbolId) => readScanValue(scanData, scans[axis], symbolId))
    );
  });

  const scored = symbols.map((symbol, index) => {
    const axes = {};
    let composite = 0;

    activeAxes.forEach((axis) => {
      const normalized = normalizedByAxis[axis][index];
      composite +=
        (activeWeight > 0
          ? weighting[axis] / activeWeight
          : 1 / activeAxes.length) * normalized;
      axes[axis] = {
        scan: scans[axis],
        value: readScanValue(scanData, scans[axis], symbol),
        normalized: normalized
      };
    });

    return { symbol: symbol, composite: composite, axes: axes, index: index };
  });

  // `index` breaks ties explicitly rather than leaning on `Array#sort` being
  // stable. It is, per spec, in every engine this ships to — but stating the
  // tie-break in the comparator is what makes "deterministic, ties keep the
  // pool's order" a property of this function rather than of its host.
  scored.sort((a, b) =>
    a.composite === b.composite ? a.index - b.index : b.composite - a.composite
  );

  return scored.map(({ symbol, composite, axes }) => ({
    symbol: symbol,
    composite: composite,
    axes: axes
  }));
}

export default rankPool;
